import React from "react";
import PropTypes from "prop-types";
import cname from "./style.module.scss";
import { Icons } from "@learningblocks/exp-constants";
import { withTranslation } from "react-i18next";
import warningIcon from "@common/images/warning.svg";
import { VOLUME_CHART } from "../MicroPipette.constants";
import { processFPUInput } from "./FloatingPointInput";

const { AlertLargeSuccess: YesTip, AlertLargeFail: NoTip } = Icons;
const TRANSLATION_KEY = {
  SET_VOLUME: "SetVolume.string",
  P2_VOLUME: "P2MicropipetteVolume.string",
  P20_VOLUME: "P20MicropipetteVolume.string",
  P200_VOLUME: "P200MicropipetteVolume.string",
  P1000_VOLUME: "P1000MicropipetteVolume.string",
  RESET_VOLUME: "ResetVolume.string",
  ADJUST_VOLUME: "AdjustVolume.string",
  MICROPIPETTE_VOLUME: "MicropipetteVolume.string",
  TIP_ATTACHED: "TipAttached.string",
  YES: "Yes.string",
  NO: "No.string",
  WARNING_HEADDING: "WarningHeadding.string",
  WARNING_TEXT: "WarningText.string",
  MIN_VOL_WARNING: "MinimumVolumeWarning.string",
  MAX_VOL_WARNING: "MaximumVolumeWarning.string",
};

class SectionLeft extends React.Component {
  constructor(props) {
    super(props);
    this.volumeInput = React.createRef();
    this.state = {
      volume: props.volume,
      inputWidth: `${props.volume}`.length,
      tempVolume: props.volume,
    };
  }

  setVolumeInputValue = (value) => {
    if (this.volumeInput?.current) {
      this.volumeInput.current.value = processFPUInput(
        this.volumeInput.current.value,
        value
      );
    }
  };

  componentDidMount() {
    this.setVolumeInputValue(this.props.volume);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.volume !== this.props.volume &&
      this.props.volume !== this.state.volume
    ) {
      this.setState({
        volume: this.props.volume,
        tempVolume: this.props.volume,
        inputWidth: `${this.props.volume}`.length,
      });
      this.setVolumeInputValue(this.props.volume);
    }
  }

  preventInvalidInput = (e) => {
    if (e.key === "-") {
      e.preventDefault();
    }
  };

  onChangeVolume = async (event) => {
    if (this.props.hasTip) {
      const { SimManager } = await import("@learningblocks/simulation-engine");
      SimManager.getInstance().addToast(
        "Contamination!",
        "<b>To avoid contamination,</b> put the tip on after setting the volume. Remove the current micropipette tip, set the volume, and then attach a new tip.",
        "Contamination"
      );
      return;
    }

    const value = event.target.valueAsNumber;
    const invalidVolume =
      this.validateVol(value) === "min" || this.validateVol(value) === "max";
    this.props.disableSaveButton(invalidVolume);
    if (value || value === 0) {
      this.setVolumeInputValue(value);
      this.setState({ tempVolume: value }, () => {
        this.props.setVolume(this.state.tempVolume);
      });
    }
  };

  validateVol(value) {
    if (value < this.props.minVolume) return "min";
    if (value > this.props.maxVolume) return "max";
    return "";
  }

  setTipStatusIcon = () => {
    if (!this.props.hasTip)
      return (
        <YesTip className={cname.status_yes} style={{ fill: "#84BE60" }} />
      );
    else
      return <NoTip className={cname.status_no} style={{ fill: "#F15D5A" }} />;
  };

  getHeading = () => {
    let heading = "";

    switch (this.props.type) {
      case "P2":
        heading = this.props.t(TRANSLATION_KEY.P2_VOLUME);
        break;
      case "P20":
        heading = this.props.t(TRANSLATION_KEY.P20_VOLUME);
        break;
      case "P200":
        heading = this.props.t(TRANSLATION_KEY.P200_VOLUME);
        break;
      case "P1000":
        heading = this.props.t(TRANSLATION_KEY.P1000_VOLUME);
        break;
      default:
    }

    return heading;
  };

  render() {
    const status = this.props.hasTip ? "yes" : "no";
    const invalidVolume =
      this.validateVol(this.state.tempVolume) === "min" ||
      this.validateVol(this.state.tempVolume) === "max";
    return (
      <div className={cname.sectionLeft}>
        <h4 className={cname.controlHeading}> {this.getHeading()}</h4>
        <h5 className={cname.controlSubHeading}>
          {" "}
          {this.props.t(TRANSLATION_KEY.ADJUST_VOLUME)}
        </h5>
        <div className={cname.volumeInputGroup}>
          <p className={cname.volumeInputLabel}>
            {this.props.t(TRANSLATION_KEY.MICROPIPETTE_VOLUME)}
          </p>
          <label
            className={
              cname[`volumeInputWrapper${invalidVolume ? "_error" : ""}`]
            }
          >
            <input
              type="number"
              min={0}
              step={VOLUME_CHART[this.props.type].rate}
              ref={this.volumeInput}
              onChange={this.onChangeVolume}
              className={cname.volumeInput}
              onKeyDown={this.preventInvalidInput}
            />
            μL
          </label>
          {invalidVolume && (
            <p className={cname.errorLabel}>
              {this.validateVol(this.state.tempVolume) === "min"
                ? this.props.t(TRANSLATION_KEY.MIN_VOL_WARNING, {
                    volume: this.props.minVolume,
                  })
                : this.props.t(TRANSLATION_KEY.MAX_VOL_WARNING, {
                    volume: this.props.maxVolume,
                  })}
            </p>
          )}
          <span
            className={cname.resetVolume}
            role="button"
            tabIndex={0}
            onClick={() => {
              this.setState({
                volume: this.props.minVolume,
                tempVolume: this.props.minVolume,
                inputWidth: `${this.props.minVolume}`.length,
              });
              this.setVolumeInputValue(this.props.minVolume);
              this.props.setDefaultVolume();
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                this.setState({
                  volume: this.props.minVolume,
                  tempVolume: this.props.minVolume,
                  inputWidth: `${this.props.minVolume}`.length,
                });
                this.setVolumeInputValue(this.props.minVolume);
                this.props.setDefaultVolume();
              }
            }}
          >
            {this.props.t(TRANSLATION_KEY.RESET_VOLUME)}
          </span>
        </div>
        <div className={cname.warning}>
          <h5 className={cname.warningHeading}>
            <img src={warningIcon} alt="" />
            {this.props.t(TRANSLATION_KEY.WARNING_HEADDING)}
          </h5>
          <h6 className={cname.warningSubHeading}>
            {this.props.t(TRANSLATION_KEY.WARNING_TEXT)}
          </h6>
          <div className={cname[`tipStatus_${status}`]}>
            <p>
              {this.props.t(TRANSLATION_KEY.TIP_ATTACHED)}{" "}
              {this.props.hasTip
                ? this.props.t(TRANSLATION_KEY.YES)
                : this.props.t(TRANSLATION_KEY.NO)}
            </p>
            {this.setTipStatusIcon()}
          </div>
        </div>
      </div>
    );
  }
}

SectionLeft.propTypes = {
  /**
   * Boolean for if Tip is attached
   */
  hasTip: PropTypes.bool,
  /**
   * Currently set volume of MicroPipette
   */
  volume: PropTypes.number,
  /**
   * Callback to set volume value
   */
  setVolume: PropTypes.func,
  /**
   * Min possible value for volume
   */
  minVolume: PropTypes.number,
  /**
   * Max possible value for volume
   */
  maxVolume: PropTypes.number,
  /**
   * Update rate for input
   */
  updateRate: PropTypes.number,
};

SectionLeft.defaultProps = {
  hasTip: false,
};

export default withTranslation()(SectionLeft);
